import { useState } from 'react';
import { Alert, Button, Checkbox, Drawer, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import PersonOutline from '@mui/icons-material/PersonOutlined';
import SmartToyOutlined from '@mui/icons-material/SmartToyOutlined';
import ViewColumnOutlined from '@mui/icons-material/ViewColumnOutlined';
import { DataTable, type Column } from '../components/DataTable';
import { FilterBar, FilterSelect, PagedFooter } from '../components/ListControls';
import { SrbCode } from '../components/SrbCode';
import { StatusChip } from '../components/StatusChip';
import { ActivityDetail } from '../features/activity/ActivityDetail';
import {
  ACTIVITY_TYPES,
  dateOf,
  whenOf,
  LOG_STATUSES,
  LOG_TONE,
  TYPE_TONE,
  type LogEntry,
} from '../features/activity/sample';
import { inPeriod, type Period } from '../lib/period';
import { PeriodToggle } from '../components/PeriodToggle';
import { TitleBar } from '../components/TitleBar';
import { useActivity } from '../features/activity/useActivity';
import { usePaging } from '../lib/usePaging';

const SIZE = 20;
const RELATED = ['Customer', 'Agent', 'Carrier', 'None'];

type Col = { key: string; always?: boolean; col: Column<LogEntry> };
const COLUMNS: Col[] = [
  {
    key: 'Date & Time',
    always: true,
    col: { header: 'Date & Time', primary: true, nowrap: true, render: whenOf },
  },
  {
    key: 'Performed By',
    col: {
      header: 'Performed By',
      nowrap: true,
      render: (e) => (
        <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center' }}>
          {e.by === 'Claude' ? <SmartToyOutlined fontSize="small" /> : <PersonOutline fontSize="small" />}
          <span>{e.by}</span>
        </Stack>
      ),
    },
  },
  {
    key: 'Type',
    col: { header: 'Type', nowrap: true, render: (e) => <StatusChip label={e.type} tone={TYPE_TONE[e.type]} /> },
  },
  { key: 'SRB', col: { header: 'SRB', nowrap: true, render: (e) => (e.srb ? <SrbCode code={e.srb} /> : '—') } },
  { key: 'Related Record', col: { header: 'Related Record', render: (e) => e.related || '—' } },
  { key: 'Activity', always: true, col: { header: 'Activity', render: (e) => e.activity } },
  { key: 'Details', col: { header: 'Details', render: (e) => e.details } },
  {
    key: 'Status',
    always: true,
    col: {
      header: 'Status',
      nowrap: true,
      action: true,
      render: (e) => <StatusChip label={e.status} tone={LOG_TONE[e.status]} />,
    },
  },
];

/** Everything Claude did and everything you decided, newest first. A click opens the entry. */
export function ActivityHistoryPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const paging = usePaging(SIZE);
  const [period, setPeriod] = useState<Period>('This Month');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [status, setStatus] = useState('all');
  const [by, setBy] = useState('all');
  const [related, setRelated] = useState('all');
  const [hidden, setHidden] = useState<string[]>([]);
  const [menu, setMenu] = useState<HTMLElement | null>(null);
  const filter = paging.reset;

  const { items: all, loading, error } = useActivity();
  const w = search.trim().toLowerCase();
  const shown = all
    .filter((e) => inPeriod(dateOf(e), period))
    .filter((e) => type === 'all' || e.type === type)
    .filter((e) => status === 'all' || e.status === status)
    .filter((e) => by === 'all' || e.by === by)
    .filter((e) => related === 'all' || e.relatedKind === related)
    .filter((e) => !w || [e.srb, e.related, e.activity, e.details, e.type].join(' ').toLowerCase().includes(w));
  const rows = paging.slice(shown);
  const open = all.find((e) => e.id === id);
  const clear = () => {
    setSearch('');
    setType('all');
    setStatus('all');
    setBy('all');
    setRelated('all');
    paging.setPage(1);
  };

  return (
    <Stack spacing={2}>
      <TitleBar
        title="Activity History"
        subtitle="Track everything Claude does across SERB. See what was done, when it was done, and what changed."
      >
        <PeriodToggle value={period} onChange={filter(setPeriod)} />
      </TitleBar>

      <FilterBar
        placeholder="Search by shipment, customer, agent, carrier, activity…"
        search={search}
        onSearch={filter(setSearch)}
      >
        <FilterSelect label="Activity Type" value={type} onChange={filter(setType)} options={ACTIVITY_TYPES} />
        <FilterSelect label="Status" value={status} onChange={filter(setStatus)} options={LOG_STATUSES} />
        <FilterSelect label="Performed By" value={by} onChange={filter(setBy)} options={['Claude', 'You']} />
        <FilterSelect label="Related Records" value={related} onChange={filter(setRelated)} options={RELATED} />
        <Button onClick={clear}>Clear All</Button>
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          startIcon={<ViewColumnOutlined />}
          onClick={(e) => setMenu(e.currentTarget)}
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
        >
          Columns
        </Button>
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
          {COLUMNS.filter((c) => !c.always).map((c) => (
            <MenuItem
              key={c.key}
              onClick={() => setHidden((h) => (h.includes(c.key) ? h.filter((k) => k !== c.key) : [...h, c.key]))}
            >
              <Checkbox edge="start" checked={!hidden.includes(c.key)} />
              <ListItemText primary={c.key} />
            </MenuItem>
          ))}
        </Menu>
      </FilterBar>

      {error && <Alert severity="error">{error}</Alert>}
      <DataTable<LogEntry>
        rows={rows}
        rowKey={(e) => e.id}
        empty={loading ? 'Loading…' : all.length ? 'No activity matches.' : 'No activity yet.'}
        onRowClick={(e) => void navigate(`/history/${e.id}`)}
        selected={(e) => e.id === id}
        columns={COLUMNS.filter((c) => !hidden.includes(c.key)).map((c) => c.col)}
      />
      <PagedFooter noun="activities" total={shown.length} page={paging.page} size={SIZE} onPage={paging.setPage} />

      <Drawer
        anchor="right"
        open={Boolean(open)}
        onClose={() => void navigate('/history')}
        slotProps={{ paper: { sx: { width: { xs: '100%', sm: 480 } } } }}
      >
        {open && <ActivityDetail e={open} onClose={() => void navigate('/history')} />}
      </Drawer>
    </Stack>
  );
}
